// packages/core/src/sage-action.ts
//
// A Start button Sage can put under a reply: the wire shape, and the check
// that every surface runs before it renders one.
//
// QA finding 7 (2026-10): asked "What should I study next?", Sage recommended
// a short Boundaries drill and gave the student nothing to click. Launching
// anything took two chained tool calls, and the model was trusted to copy a URL
// from one into the other. So an action is now BUILT BY THE SERVER (the
// offer_activity tool, and the per-question chat route for 'practice'),
// persisted with the tool result, and re-checked here by every renderer — the
// web card, the mobile button, and the server itself before it returns one. The
// model never writes the href.
//
// Why a per-kind allow-list rather than "starts with /": an action is a link a
// student clicks without a confirm step, it never expires, and old ones are
// replayed from history. Each kind therefore names the one page it may open and
// the exact query keys that page reads, so a stored row can't be bent into
// "/practice/session?planDay=…" (which ticks a study-plan day) or a link off
// the site.
//
// Lives in core because the phone renders these too, so it must run unchanged
// in Hermes: hand-rolled path/query parsing, no URL or URLSearchParams (React
// Native's URL polyfill doesn't implement the getters this would need).

/** Every kind of action a renderer accepts. */
export const SAGE_ACTION_KINDS = [
  'drill',
  'lesson',
  'skill_check',
  'first_step',
  'practice',
] as const
export type SageActionKind = (typeof SAGE_ACTION_KINDS)[number]

export type SageActionDifficulty = 'E' | 'M' | 'H' | 'mixed'
const DIFFICULTIES: readonly SageActionDifficulty[] = ['E', 'M', 'H', 'mixed']

export type SageAction = {
  /** Shape version. A renderer drops any version it doesn't know. */
  v: 1
  kind: SageActionKind
  /** The topic as the student sees it ("Boundaries", "Punctuation"). Null for first_step. */
  topic: string | null
  /** Questions in the session — the number actually picked, never the number asked for. */
  count: number | null
  /** Drill difficulty; null where the kind has none. */
  difficulty: SageActionDifficulty | null
  /** Same-origin path. Always server-built; see isSageActionHref for what each kind may open. */
  href: string
  /** What it is: "5 Boundaries questions", "Boundaries skill check". */
  label: string
  /** The button text: "Start", "Start Checkpoint". */
  cta: string
  /** One optional line under the label ("About 1 hour"). */
  note: string | null
}

/**
 * Sage drill size. Default 5 (the size Sage drills have always been, and the
 * prompt's example); 15 the most that still feels like a quick set. The floor
 * was 3 ("the smallest set worth a session") — but a student who asks for ONE
 * question got a reply about one question over a card holding three, because
 * offer_activity clamped the ask silently (QA, 2026-10-10). The runner plays
 * a one-id drill fine, so the floor is what the student asked for.
 */
export const DRILL_COUNT = { min: 1, max: 15, default: 5 } as const

/** /api/practice/start serves at most this many ids in one session. */
export const MAX_DRILL_IDS = 25

/**
 * A learning-path skill check is always ten questions. Mirrors CHECKPOINT_COUNT
 * in src/lib/learn/learning-path.ts (pinned equal by a test there — core can't
 * import from src/).
 */
export const SKILL_CHECK_COUNT = 10

/** Topic practice offered under a per-question Sage reply. */
export const PRACTICE_COUNT = { min: 1, max: 50, default: 5 } as const

/**
 * The `from` a per-question 'practice' action carries.
 *
 * Deliberately NOT 'sage': /practice/session reads `from` only to pick its
 * "Back to" target (resolveBackTarget in src/lib/study-plan/
 * study-plan-completion.ts), and 'sage' means "back to /tutor" — a page the
 * student never opened, since this chat lived inline in a practice or review
 * screen. An unknown `from` falls through to the Topics hub, which is where
 * topic practice lives. It is also non-empty, so isSageDrill (src/lib/practice/
 * sage-drill.ts) can never mistake the session for an ids drill: it treats a
 * MISSING `from` as an old Sage drill, and this session has no ids anyway.
 */
export const SAGE_PRACTICE_FROM = 'sage-chat'

const TOPIC_MAX = 120
const LABEL_MAX = 120
const CTA_MAX = 40
const NOTE_MAX = 200
const HREF_MAX = 2048

/* ------------------------------------------------------------------ */
/*  Href checks                                                         */
/* ------------------------------------------------------------------ */

// RFC 3986 path/query characters only. Leaves out whitespace and control
// characters (browsers strip tabs/newlines, so "/\t/evil.com" would become
// "//evil.com"), backslash (browsers read "/\evil.com" as "//evil.com"), and
// '#', which no builder produces.
const HREF_CHARS = /^[A-Za-z0-9\-._~%!$'()*+,;=:@/?&]+$/

type LocalHref = { path: string; params: Map<string, string> }

/**
 * Split a same-origin path into its path and decoded query. Null for anything
 * that isn't one: absolute and protocol-relative URLs, schemes like
 * "javascript:", repeated query keys, a malformed escape.
 */
function parseLocalHref(href: unknown): LocalHref | null {
  if (typeof href !== 'string' || href.length === 0 || href.length > HREF_MAX) return null
  if (!href.startsWith('/') || href.startsWith('//')) return null
  if (!HREF_CHARS.test(href)) return null
  const q = href.indexOf('?')
  const path = q === -1 ? href : href.slice(0, q)
  const params = new Map<string, string>()
  if (q === -1) return { path, params }
  const query = href.slice(q + 1)
  if (query === '' || query.includes('?')) return null
  for (const pair of query.split('&')) {
    const eq = pair.indexOf('=')
    if (eq <= 0) return null
    const key = pair.slice(0, eq)
    // A repeated key is ambiguous (which one does the page read?), so refuse it.
    if (params.has(key)) return null
    try {
      // '+' is a space in a query (URLSearchParams writes spaces that way).
      params.set(key, decodeURIComponent(pair.slice(eq + 1).replace(/\+/g, ' ')))
    } catch {
      return null
    }
  }
  return { path, params }
}

function onlyKeys(params: Map<string, string>, allowed: readonly string[]): boolean {
  for (const key of params.keys()) if (!allowed.includes(key)) return false
  return true
}

function nonEmpty(value: string | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

/** A decimal integer string within [min, max]. */
function intInRange(value: string | undefined, min: number, max: number): boolean {
  if (typeof value !== 'string' || !/^\d{1,3}$/.test(value)) return false
  const n = Number(value)
  return n >= min && n <= max
}

const DRILL_ID = /^[^\s,]{1,100}$/

/** The question ids of a drill href, or null when they aren't 1–25 well-formed ids. */
function drillIds(params: Map<string, string>): string[] | null {
  const raw = params.get('ids')
  if (!nonEmpty(raw)) return null
  const ids = raw.split(',')
  if (ids.length < 1 || ids.length > MAX_DRILL_IDS) return null
  return ids.every((id) => DRILL_ID.test(id)) ? ids : null
}

const LESSON_PATH = /^\/learn\/[a-z0-9-]+$/
const FIRST_STEP_PATHS = ['/checkpoint', '/sat']

function hrefMatchesKind(kind: SageActionKind, parsed: LocalHref): boolean {
  const { path, params } = parsed
  switch (kind) {
    case 'drill': {
      // sageDrillHref: a fixed list of ids that ends after the last one, and
      // from=sage so the session ends with "Practice more" and "Back to Sage".
      if (path !== '/practice/session') return false
      if (!onlyKeys(params, ['ids', 'label', 'from', 'drillTopic', 'drillDifficulty'])) return false
      if (params.get('from') !== 'sage') return false
      const difficulty = params.get('drillDifficulty')
      if (difficulty !== undefined && !(DIFFICULTIES as readonly string[]).includes(difficulty)) {
        return false
      }
      return drillIds(params) !== null
    }
    case 'skill_check':
      // checkpointHref: ten questions on a path topic; from=learn sends "Back" to the path.
      return (
        path === '/practice/session' &&
        onlyKeys(params, ['topic', 'count', 'from']) &&
        nonEmpty(params.get('topic')) &&
        params.get('count') === String(SKILL_CHECK_COUNT) &&
        params.get('from') === 'learn'
      )
    case 'practice':
      return (
        path === '/practice/session' &&
        onlyKeys(params, ['topic', 'count', 'from']) &&
        nonEmpty(params.get('topic')) &&
        intInRange(params.get('count'), PRACTICE_COUNT.min, PRACTICE_COUNT.max) &&
        params.get('from') === SAGE_PRACTICE_FROM
      )
    case 'lesson':
      return LESSON_PATH.test(path) && params.size === 0
    case 'first_step':
      // The Checkpoint or the full SAT; `assigned=1` when a teacher set it
      // (assignmentHref in src/lib/school/assignments.ts).
      if (!FIRST_STEP_PATHS.includes(path)) return false
      return params.size === 0 || (params.size === 1 && params.get('assigned') === '1')
    default:
      return false
  }
}

function isKind(kind: unknown): kind is SageActionKind {
  return typeof kind === 'string' && (SAGE_ACTION_KINDS as readonly string[]).includes(kind)
}

/**
 * Whether `href` is a page an action of this kind may open: a same-origin path
 * (starts with "/", not "//", no backslash, whitespace or fragment) on that
 * kind's allow-list —
 *   drill        /practice/session?ids=<1–25 ids>&from=sage[&label&drillTopic&drillDifficulty]
 *   skill_check  /practice/session?topic=<topic>&count=10&from=learn
 *   practice     /practice/session?topic=<topic>&count=<1–50>&from=sage-chat
 *   lesson       /learn/<slug>          (slug: [a-z0-9-]+)
 *   first_step   /checkpoint or /sat, optionally ?assigned=1
 * No other query keys are accepted on any of them.
 */
export function isSageActionHref(kind: unknown, href: unknown): boolean {
  if (!isKind(kind)) return false
  const parsed = parseLocalHref(href)
  return parsed !== null && hrefMatchesKind(kind, parsed)
}

/* ------------------------------------------------------------------ */
/*  The action                                                          */
/* ------------------------------------------------------------------ */

function optionalText(value: unknown, max: number): string | null | undefined {
  if (value === null || value === undefined) return null
  if (typeof value !== 'string') return undefined
  const t = value.trim()
  if (t.length === 0) return null
  return t.length <= max ? t : undefined
}

function requiredText(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null
  const t = value.trim()
  return t.length > 0 && t.length <= max ? t : null
}

/**
 * The action in `raw`, or null when it isn't one a surface may render. Runs on
 * the server before an action is returned and on every client before a card is
 * drawn, so a row persisted under older rules, a hand-edited row, or a bug in a
 * builder renders nothing rather than a wrong button.
 *
 * Beyond the href allow-list, the action must agree with its own href: a
 * drill's count is the number of ids it opens and its topic/difficulty are the
 * ones the session will extend with "Practice more"; a skill check is exactly
 * SKILL_CHECK_COUNT questions on the topic it names; a practice action's count
 * and topic are the ones in its link. A label that says "5" on a link that
 * opens 3 is exactly the bug this exists to stop.
 */
export function parseSageAction(raw: unknown): SageAction | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const a = raw as Record<string, unknown>
  if (a.v !== 1 || !isKind(a.kind)) return null
  const kind = a.kind

  const label = requiredText(a.label, LABEL_MAX)
  const cta = requiredText(a.cta, CTA_MAX)
  if (!label || !cta) return null
  const note = optionalText(a.note, NOTE_MAX)
  const topic = optionalText(a.topic, TOPIC_MAX)
  if (note === undefined || topic === undefined) return null

  let count: number | null
  if (a.count === null || a.count === undefined) count = null
  else if (typeof a.count === 'number' && Number.isInteger(a.count)) count = a.count
  else return null

  let difficulty: SageActionDifficulty | null
  if (a.difficulty === null || a.difficulty === undefined) difficulty = null
  else if ((DIFFICULTIES as readonly unknown[]).includes(a.difficulty)) {
    difficulty = a.difficulty as SageActionDifficulty
  } else return null

  const href = a.href
  if (typeof href !== 'string') return null
  const parsed = parseLocalHref(href)
  if (!parsed || !hrefMatchesKind(kind, parsed)) return null
  const { params } = parsed

  switch (kind) {
    case 'drill': {
      const ids = drillIds(params)
      if (!topic || !difficulty || count === null || !ids) return null
      if (count < 1 || count > DRILL_COUNT.max || count !== ids.length) return null
      const drillTopic = params.get('drillTopic')
      if (drillTopic !== undefined && drillTopic !== topic) return null
      const drillDifficulty = params.get('drillDifficulty')
      if (drillDifficulty !== undefined && drillDifficulty !== difficulty) return null
      break
    }
    case 'skill_check':
      if (!topic || params.get('topic') !== topic || count !== SKILL_CHECK_COUNT) return null
      break
    case 'practice':
      if (!topic || params.get('topic') !== topic) return null
      if (count === null || String(count) !== params.get('count')) return null
      break
    case 'lesson':
      if (!topic || count !== null) return null
      break
    case 'first_step':
      if (topic !== null || count !== null) return null
      break
  }

  return { v: 1, kind, topic, count, difficulty, href, label, cta, note }
}

/**
 * The href of a 'practice' action: topic practice of `count` questions, Back to
 * Topics when done (see SAGE_PRACTICE_FROM). The caller passes a count within
 * PRACTICE_COUNT and runs parseSageAction on the action it builds.
 */
export function buildPracticeHref(topic: string, count: number): string {
  return `/practice/session?topic=${encodeURIComponent(topic)}&count=${count}&from=${SAGE_PRACTICE_FROM}`
}

/**
 * The data-track value on a Start link — "sage_action:drill:Boundaries".
 * ClientAnalytics stores it as a click event, so web and mobile must agree on it.
 */
export function sageActionTrackId(action: Pick<SageAction, 'kind' | 'topic'>): string {
  return `sage_action:${action.kind}:${action.topic ?? ''}`
}
